import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { InputFieldset } from 'ui/inputFieldset';
import { SelectFieldset } from 'ui/selectFieldset';
import { SelectTriggerUI } from 'ui/selectTrigger';
import { CheckboxFieldset } from "ui/checkboxFieldset";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  FileText,
  Plus,
  Search,
  Edit2,
  // Trash2,
  X,
  FileStack,
  Check,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "../../ui/checkbox";
import {
  ApiDocument,
  ApiScreen,
  createDocument,
  CreateDocumentPayload,
  DocumentSummary,
  getDocuments,
  getDocumentScreens,
  getDocumentSummary,
  updateDocument,
  UpdateDocumentPayload,
} from "../../services/documentService";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
import Pagination from "ui/pagination";

interface Document {
  id: string;
  name: string;
  code: string;
  modules: string[];
  moduleLocations: Record<string, string>;
  mandatory: boolean;
  acceptedFormats: string[];
  maxSize: number;
  expiryMonths?: number;
  status: "active" | "inactive";
}

type ModuleType =
  | "registration"
  | "visa"
  | "accommodation" 
  | "transport"
  | "tours"
  | "add_renewal"
  | "tours_document"
  | "tours_tickets_upload"
  | "registration_approval";

export const DocumentMasterPage = () => {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);

  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [screens, setScreens] = useState<ApiScreen[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [summary, setSummary] = useState<DocumentSummary | null>(null);
  
  const { canAdd, canEdit, screenName, icon: ScreenIcon } = useScreenPermission();

  const SCREEN_COLOR_MAP: Record<string, ModuleType> = {
    tour_operator_registration: "registration",
    visa_add_applicant: "visa",
    accommodation: "accommodation",
    transport: "transport",
    tours: "tours",
    to_renewal_approval: "add_renewal",
    tours_document: "tours_document",
    tours_tickets_upload: "tours_tickets_upload",
    to_registration_approval: "registration_approval",
  };

  const [filters, setFilters] = useState({
    name: "",
    code: "",
    module: "all",
    mandatory: "all",
    format: "all",
    status: "all",
  });

  const [errors, setErrors] = useState<{
    name?: string;
    code?: string;
    modules?: string;
    acceptedFormats?: string;
    maxSize?: string;
  }>({});

  const validateAddForm = () => {
    const newErrors: typeof errors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Document name is required";
    } else if (!/^[A-Za-z ]{1,20}$/.test(formData.name.trim())) {
      newErrors.name =
        "Document name should contain only letters and spaces and maximum 20 characters";
    }

    if (!formData.code.trim()) {
      newErrors.code = "Document code is required";
    } else if (!/^[A-Za-z_]{1,20}$/.test(formData.code.trim())) {
      newErrors.code =
        "Document code should contain only letters and underscore (_) and maximum 20 characters";
    }

    if (formData.modules.length === 0) {
      newErrors.modules = "At least one screen must be selected";
    }

    if (formData.acceptedFormats.length === 0) {
      newErrors.acceptedFormats = "Select at least one file format";
    }

    if (!formData.maxSize || formData.maxSize < 1) {
      newErrors.maxSize = "Maximum file size must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const mapApiDocumentToUi = (api: ApiDocument): Document => {
    return {
      id: String(api.documentId),
      name: api.documentName,
      code: api.documentCode,
      modules: api.screenNames.map((s) => s.toLowerCase().replace(/\s+/g, "_")),
      moduleLocations: {},
      mandatory: api.isMandatory,
      acceptedFormats: api.acceptedFormatsList.map((f) => f.toUpperCase()),
      maxSize: api.maxFileSizeMB,
      expiryMonths: api.expireMaxLimit ? Number(api.expireMaxLimit) : undefined,
      status: api.isActive ? "active" : "inactive",
    };
  };

  const fetchDocuments = async () => {
    try {
    setLoading(true);
      const res = await getDocuments({
        searchText: searchTerm || null,
        documentName: filters.name || null,
        documentCode: filters.code || null,
        isMandatory:
          filters.mandatory === "all" ? null : filters.mandatory === "required",
        isActive: filters.status === "all" ? null : filters.status === "active",
        pageNo: currentPage,
        pageSize,
      });

      setDocuments(res.items.map(mapApiDocumentToUi));
      setTotalCount(res.totalCount);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    fetchDocuments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, pageSize]);

  useEffect(() => {
    const loadScreens = async () => {
      try {
        setLoading(true);
        const res = await getDocumentScreens(true);
        setScreens(res);
      } catch {
        toast.error("Failed to load screens");
      } finally {
        setLoading(false);
      }
    };

    loadScreens();
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    modules: [] as string[],
    moduleLocations: {} as Record<string, string>,
    mandatory: true,
    acceptedFormats: ["PDF"],
    maxSize: 5,
    expiryMonths: undefined as number | undefined,
    status: "active" as "active" | "inactive",
  });

  const [editFormData, setEditFormData] = useState({
    name: "",
    code: "",
    modules: [] as string[],
    moduleLocations: {} as Record<string, string>,
    mandatory: true,
    acceptedFormats: ["PDF"],
    maxSize: 5,
    expiryMonths: undefined as number | undefined,
    status: "active" as "active" | "inactive",
  });

  const mapFormDataToApiPayload = (): CreateDocumentPayload => ({
    documentCode: formData.code,
    documentName: formData.name,
    screenNames: formData.modules,
    acceptedFormats: formData.acceptedFormats
      .map((f) => f.toLowerCase())
      .join(","),
    expireMaxLimit: formData.expiryMonths
      ? String(formData.expiryMonths)
      : undefined,
    maxFileSizeKB: formData.maxSize * 1024,
    isMandatory: formData.mandatory,
    isActive: formData.status === "active",
    description: `${formData.name} document upload`,
    createdBy: 16,
  });

  const mapEditFormDataToApiPayload = (): UpdateDocumentPayload => {
    if (!editingDocument) {
      throw new Error("No document selected for update");
    }

    return {
      documentId: Number(editingDocument.id),
      documentCode: editFormData.code,
      documentName: editFormData.name,
      screenNames: editFormData.modules,

      acceptedFormats: editFormData.acceptedFormats
        .map((f) => f.toLowerCase())
        .join(","),

      expireMaxLimit: editFormData.expiryMonths
        ? String(editFormData.expiryMonths)
        : undefined,

      maxFileSizeKB: editFormData.maxSize * 1024,
      isMandatory: editFormData.mandatory,
      isActive: editFormData.status === "active",
      description: `${editFormData.name} document upload`,
      updatedBy: 1,
    };
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.modules.some((m) =>
        m.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesName =
      !filters.name ||
      doc.name.toLowerCase().includes(filters.name.toLowerCase());
    const matchesCode =
      !filters.code ||
      doc.code.toLowerCase().includes(filters.code.toLowerCase());
    const matchesModule =
      filters.module === "all" || doc.modules.includes(filters.module);
    const matchesMandatory =
      filters.mandatory === "all" ||
      (filters.mandatory === "required" && doc.mandatory) ||
      (filters.mandatory === "optional" && !doc.mandatory);
    const matchesFormat =
      filters.format === "all" || doc.acceptedFormats.includes(filters.format);
    const matchesStatus =
      filters.status === "all" || doc.status === filters.status;

    return (
      matchesSearch &&
      matchesName &&
      matchesCode &&
      matchesModule &&
      matchesMandatory &&
      matchesFormat &&
      matchesStatus
    );
  });

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      setLoading(true);

      const res = await getDocumentSummary();
      setSummary(res);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDocument = async () => {
    const isValid = validateAddForm();
    if (!isValid) return;

    const isDuplicateCode = documents.some(
      (d) => d.code.toUpperCase() === formData.code.toUpperCase()
    );

    if (isDuplicateCode) {
      setErrors((prev) => ({
        ...prev,
        code: "Document code already exists",
      }));
      return;
    }

    try {
      setLoading(true);
      const payload = mapFormDataToApiPayload();
      await createDocument(payload);

      toast.success("Document type added successfully");

      setIsAddDialogOpen(false);
      resetForm();
      setErrors({});

      await fetchDocuments();
      await loadSummary();
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = error as any;

      const status = err?.response?.status;
      const data = err?.response?.data;

      const message =
        data?.message ||
        data?.error ||
        (Array.isArray(data?.errors) ? data.errors.join(", ") : "") ||
        (typeof data === "string" ? data : "");

      console.log("Create document error:", { status, data });

      if (
        status === 500 ||
        status === 400 ||
        message.toLowerCase().includes("duplicate") ||
        message.toLowerCase().includes("exists")
      ) {
        setErrors((prev) => ({
          ...prev,
          code: "Document code already exists",
        }));
        return;
      }

      toast.error("Failed to create document");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadSummary = async () => {
      const res = await getDocumentSummary();
      setSummary(res);
    };

    loadSummary();
  }, []);

  const handleEditDocument = (doc: Document) => {
    setEditingDocument(doc);
    setEditFormData({
      name: doc.name,
      code: doc.code,
      modules: doc.modules,
      moduleLocations: doc.moduleLocations,
      mandatory: doc.mandatory,
      acceptedFormats: doc.acceptedFormats,
      maxSize: doc.maxSize,
      expiryMonths: doc.expiryMonths,
      status: doc.status,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateDocument = async () => {
    if (!editingDocument) return;

    if (
      !editFormData.name ||
      !editFormData.code ||
      editFormData.modules.length === 0 ||
      editFormData.acceptedFormats.length === 0
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);
      const payload = mapEditFormDataToApiPayload();
 
      await updateDocument(payload);

      toast.success("Document type updated successfully");

      setIsEditDialogOpen(false);
      setEditingDocument(null);

      fetchDocuments();
    } catch (error) {
      toast.error("Failed to update document");
    } finally {
      setLoading(false);
    }
  };

  // const handleDeleteDocument = (id: string) => {
  //   if (confirm('Are you sure you want to delete this document type?')) {
  //     setDocuments(documents.filter(doc => doc.id !== id));
  //     toast.success('Document type deleted successfully');
  //   }
  // };

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      modules: [],
      moduleLocations: {},
      mandatory: true,
      acceptedFormats: ["PDF"],
      maxSize: 5,
      expiryMonths: undefined,
      status: "active",
    });
  };

  const toggleFormatSelection = (format: string, isEdit = false) => {
    if (isEdit) {
      setEditFormData((prev) => ({
        ...prev,
        acceptedFormats: prev.acceptedFormats.includes(format)
          ? prev.acceptedFormats.filter((f) => f !== format)
          : [...prev.acceptedFormats, format],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        acceptedFormats: prev.acceptedFormats.includes(format)
          ? prev.acceptedFormats.filter((f) => f !== format)
          : [...prev.acceptedFormats, format],
      }));
    }
  };

  const toggleModuleSelection = (moduleValue: string, isEdit = false) => {
    if (isEdit) {
      setEditFormData((prev) => ({
        ...prev,
        modules: prev.modules.includes(moduleValue)
          ? prev.modules.filter((m) => m !== moduleValue)
          : [...prev.modules, moduleValue],
        moduleLocations: {
          ...prev.moduleLocations,
          [moduleValue]: `uploads/${moduleValue}/${prev.code.toLowerCase().replace(/_/g, "-")}`,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        modules: prev.modules.includes(moduleValue)
          ? prev.modules.filter((m) => m !== moduleValue)
          : [...prev.modules, moduleValue],
        moduleLocations: {
          ...prev.moduleLocations,
          [moduleValue]: `uploads/${moduleValue}/${prev.code.toLowerCase().replace(/_/g, "-")}`,
        },
      }));
    }
  };

  const getModuleLabel = (screenKey: string) => {
    return (
      screens.find((s) => s.screenKey === screenKey)?.screenName || screenKey
    );
  };

  const getModuleColor = (moduleValue: string): string => {
    const colorKey = SCREEN_COLOR_MAP[moduleValue];

    const colors: Record<ModuleType, string> = {
      registration: "bg-blue-100 text-blue-700 border-blue-300",
      visa: "bg-purple-100 text-purple-700 border-purple-300",
      accommodation: "bg-green-100 text-green-700 border-green-300",
      transport: "bg-orange-100 text-orange-700 border-orange-300",
      tours: "bg-pink-100 text-pink-700 border-pink-300",
      add_renewal: "bg-cyan-100 text-cyan-700 border-cyan-300",
      tours_document: "bg-indigo-100 text-indigo-700 border-indigo-300",
      tours_tickets_upload: "bg-amber-100 text-amber-700 border-amber-300",
      registration_approval:
        "bg-emerald-100 text-emerald-700 border-emerald-300",
    };

    return colorKey
      ? colors[colorKey]
      : "bg-gray-100 text-gray-700 border-gray-300";
  };

  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    if (!sortColumn) return 0;

    let aValue: string | number = "";
    let bValue: string | number = "";

    if (sortColumn === "name") {
      aValue = a.name;
      bValue = b.name;
    } else if (sortColumn === "code") {
      aValue = a.code;
      bValue = b.code;
    } else if (sortColumn === "maxSize") {
      aValue = a.maxSize;
      bValue = b.maxSize;
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-4">
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
  
              <Input
                placeholder="Search by document name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="h-10 pl-9 pr-9 shadow-sm"
              />
  
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm("");
                    setCurrentPage(1);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowStatistics(!showStatistics)}
              className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
            >
              {showStatistics ? (
                <ArrowUp className="w-5 h-5" />
              ) : (
                <BarChart3 className="w-5 h-5" />
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={
                showFilters
                  ? "text-white"
                  : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
              }
              title="Advanced Filters"
            >
              <Filter className="w-4 h-4 sm:mr-0" />
            </Button>
            {canAdd && (
              <Button
                onClick={() => setIsAddDialogOpen(true)}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Document Type
              </Button>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        {showStatistics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">Total Documents</p>
                    <p className="text-2xl font-bold text-blue-900">
                      <p>{summary?.totalDocuments ?? 0}</p>
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Active Documents</p>
                    <p className="text-2xl font-bold text-green-900">
                      <p>{summary?.activeDocuments ?? 0}</p>
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600">Mandatory</p>
                    <p className="text-2xl font-bold text-red-900">
                      <p>{summary?.mandatoryDocuments ?? 0}</p>
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <FileStack className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600">Screens</p>
                    <p className="text-2xl font-bold text-amber-900">
                      {screens.length}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <FileStack className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Advanced Filters Section */}
        {showFilters && (
          <Card
            className="bg-white border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50"
            style={{ gap: "0px" }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle
                    style={{ color: "#5B9BD5" }}
                    className="flex items-center gap-2 text-lg"
                  >
                    <Filter className="w-5 h-5" />
                    Advanced Filters
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Filter documents by multiple criteria
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Document Name Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-name" className="text-sm">
                    Document Name
                  </Label>
                  <Input
                    id="filter-name"
                    placeholder="Search by name..."
                    value={filters.name}
                    onChange={(e) =>
                      setFilters({ ...filters, name: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>

                {/* Document Code Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-code" className="text-sm">
                    Document Code
                  </Label>
                  <Input
                    id="filter-code"
                    placeholder="Search by code..."
                    value={filters.code}
                    onChange={(e) =>
                      setFilters({ ...filters, code: e.target.value })
                    }
                    className="font-mono h-9 text-sm"
                  />
                </div>

                {/* Module Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-module" className="text-sm">
                    Module
                  </Label>
                  <Select
                    value={filters.module}
                    onValueChange={(value) =>
                      setFilters({ ...filters, module: value })
                    }
                  >
                    <SelectTrigger id="filter-module" className="h-9 text-sm">
                      <SelectValue placeholder="All modules" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All modules</SelectItem>
                      {screens.map((screen) => (
                        <div
                          key={screen.screenKey}
                          className="flex items-center gap-2"
                        >
                          <Checkbox
                            checked={editFormData.modules.includes(
                              screen.screenKey
                            )}
                            onCheckedChange={() =>
                              toggleModuleSelection(screen.screenKey, true)
                            }
                          />
                          <Label className="cursor-pointer text-sm font-normal">
                            {screen.screenName}
                          </Label>
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Mandatory Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-mandatory" className="text-sm">
                    Requirement
                  </Label>
                  <Select
                    value={filters.mandatory}
                    onValueChange={(value) =>
                      setFilters({ ...filters, mandatory: value })
                    }
                  >
                    <SelectTrigger id="filter-mandatory" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Documents</SelectItem>
                      <SelectItem value="required">Mandatory Only</SelectItem>
                      <SelectItem value="optional">Optional Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Format Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-format" className="text-sm">
                    File Format
                  </Label>
                  <Select
                    value={filters.format}
                    onValueChange={(value) =>
                      setFilters({ ...filters, format: value })
                    }
                  >
                    <SelectTrigger id="filter-format" className="h-9 text-sm">
                      <SelectValue placeholder="All formats" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All formats</SelectItem>
                      <SelectItem value="PDF">PDF</SelectItem>
                      <SelectItem value="JPG">JPG</SelectItem>
                      <SelectItem value="PNG">PNG</SelectItem>
                      <SelectItem value="JPEG">JPEG</SelectItem>
                      <SelectItem value="DOC">DOC</SelectItem>
                      <SelectItem value="DOCX">DOCX</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-status" className="text-sm">
                    Status
                  </Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) =>
                      setFilters({ ...filters, status: value })
                    }
                  >
                    <SelectTrigger id="filter-status" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="inactive">Inactive Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active Filters Summary & Clear Button */}
              {(filters.name ||
                filters.code ||
                filters.module !== "all" ||
                filters.mandatory !== "all" ||
                filters.format !== "all" ||
                filters.status !== "all") && (
                <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#5B9BD5] mb-2">
                      Active Filters:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {filters.name && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Name: {filters.name}
                        </Badge>
                      )}
                      {filters.code && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Code: {filters.code}
                        </Badge>
                      )}
                      {filters.module !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Module: {getModuleLabel(filters.module)}
                        </Badge>
                      )}
                      {filters.mandatory !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          {filters.mandatory === "required"
                            ? "Mandatory"
                            : "Optional"}
                        </Badge>
                      )}
                      {filters.format !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Format: {filters.format}
                        </Badge>
                      )}
                      {filters.status !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Status: {filters.status}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFilters({
                        name: "",
                        code: "",
                        module: "all",
                        mandatory: "all",
                        format: "all",
                        status: "all",
                      });
                    }}
                    className="text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear All
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Documents Table */}
        <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px", marginTop: "10px" }}>
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center gap-1">
                        Document Name
                        {sortColumn === "name" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Screens
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Formats
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("maxSize")}
                    >
                      <div className="flex items-center gap-1">
                        Max Size
                        {sortColumn === "maxSize" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Status
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Expiry Month
                    </TableHead>
                    {canEdit && (
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-right"
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedDocuments.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center py-8 px-4 text-[#5B9BD5] font-medium"
                      >
                        Loading Document Master...
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedDocuments.map((doc) => (
                      <TableRow key={doc.id} className="hover:bg-sky-50/30">
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                              <FileText className="w-4 h-4 text-[#5B9BD5]" />
                            </div>
                            <div className="relative group max-w-[150px] overflow-visible">
                              <p className="text-xs text-gray-700 truncate">
                                {doc.name}
                              </p>
                              <div
                                className="pointer-events-none absolute bottom-full mb-2 hidden group-hover:block z-50 left-0 right-0 mx-auto min-w-[280px] 
                                  max-w-[420px] rounded-lg bg-gray-900 px-4 py-3 text-xs text-white shadow-lg whitespace-normal break-words"
                              >
                                {doc.name}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {doc.modules.map((module) => (
                              <Badge
                                key={module}
                                className={`${getModuleColor(module)} text-[10px] px-1.5 py-0.5`}
                              >
                                {getModuleLabel(module)}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {doc.acceptedFormats.map((format) => (
                              <Badge
                                key={format}
                                variant="outline"
                                className="text-[10px] border-[#5B9BD5]/30 text-[#5B9BD5] px-1.5 py-0.5"
                              >
                                {format}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-700">
                            {doc.maxSize} MB
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <Badge
                            className={`${doc.status === "active" ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                          >
                            {doc.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          {doc.expiryMonths ? (
                            <span className="text-xs text-gray-700">
                              {doc.expiryMonths} months
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </TableCell>
                        {canEdit && (
                          <TableCell className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEditDocument(doc)}
                                  className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                  title="Edit document"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </Button>
                            
                              {/* <Button
                                size="sm"
                                variant="ghost"
                                // onClick={() => handleDeleteDocument(doc.id)}
                                className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                                title="Delete document"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button> */}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3 p-4">
              {sortedDocuments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No document types found</p>
                </div>
              ) : (
                sortedDocuments.map((doc) => (
                  <Card
                    key={doc.id}
                    className="border-[#5B9BD5]/30 hover:shadow-md transition-shadow"
                  >
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                              <FileText className="w-5 h-5 text-[#5B9BD5]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-900 truncate">
                                {doc.name}
                              </h3>
                              {doc.expiryMonths && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Expiry: {doc.expiryMonths} months
                                </p>
                              )}
                            </div>
                          </div>
                          <Badge
                            className={
                              doc.status === "active"
                                ? "bg-green-100 text-green-700 border-green-300"
                                : "bg-gray-100 text-gray-700 border-gray-300"
                            }
                          >
                            {doc.status}
                          </Badge>
                        </div>

                        {/* Details */}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Mandatory:</span>
                            <Badge
                              className={
                                doc.mandatory
                                  ? "bg-red-100 text-red-700 border-red-300"
                                  : "bg-gray-100 text-gray-700 border-gray-300"
                              }
                            >
                              {doc.mandatory ? "Required" : "Optional"}
                            </Badge>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Max Size:</span>
                            <span className="font-medium text-gray-900">
                              {doc.maxSize} MB
                            </span>
                          </div>

                          <div>
                            <span className="text-gray-600 block mb-1">
                              Screens:
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {doc.modules.map((module) => (
                                <Badge
                                  key={module}
                                  className={`${getModuleColor(module)} text-xs`}
                                >
                                  {getModuleLabel(module)}
                                </Badge>
                              ))}
                            </div>
                          </div>

                          <div>
                            <span className="text-gray-600 block mb-1">
                              Formats:
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {doc.acceptedFormats.map((format) => (
                                <Badge
                                  key={format}
                                  variant="outline"
                                  className="text-xs border-[#5B9BD5]/30 text-[#5B9BD5]"
                                >
                                  {format}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-3 border-t">
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditDocument(doc)}
                              className="flex-1 text-[#5B9BD5] border-[#5B9BD5] hover:bg-sky-50"
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit
                            </Button>
                          )}
                          {/* <Button
                            size="sm"
                            variant="outline"
                            // onClick={() => handleDeleteDocument(doc.id)}
                            className="flex-1 text-red-600 border-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </Button> */}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            label="documents"
          />
        </Card>

        {/* Add Document Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="w-[60vw] !max-w-[60vw] max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Add New Document Type
              </DialogTitle>
              <DialogDescription>
                Define a new document type that can be used across multiple
                modules
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 px-6 py-4 pb-24 sm:pb-4 sm:px-1">
              <div className="space-y-2">
                <InputFieldset
                  label="Document Name"
                  required
                  id="name"
                  placeholder="e.g., Passport Copy"
                  value={formData.name}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/[^A-Za-z ]/g, "")
                      .slice(0, 20);

                    setFormData({
                      ...formData,
                      name: value,
                    });

                    setErrors((prev) => ({
                      ...prev,
                      name: undefined,
                    }));
                  }}
                />
                {errors.name && (
                  <p className="text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Document Code"
                  required
                  id="code"
                  placeholder="e.g., DOC_PASSPORT"
                  value={formData.code}
                  onChange={(e) => {
                    const value = e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z_]/g, "")
                      .slice(0, 20);

                    setFormData({
                      ...formData,
                      code: value,
                    });

                    setErrors((prev) => ({
                      ...prev,
                      code: undefined,
                    }));
                  }}
                />
                {errors.code && (
                  <p className="text-xs text-red-600">{errors.code}</p>
                )}

                <p className="text-xs text-gray-500">
                  Auto-formatted to uppercase with underscores
                </p>
              </div>

              <div className="space-y-3">
                <CheckboxFieldset
                  label="Screens"
                  required
                  description="Select one or more screens where this document can be uploaded"
                  error={errors.modules}
                >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border rounded-lg bg-gray-50">
                  {screens.map((screen) => (
                    <div
                      key={screen.screenKey}
                      className="flex items-center gap-2"
                    >
                      <Checkbox
                        checked={formData.modules.includes(screen.screenKey)}
                        onCheckedChange={() => {
                          toggleModuleSelection(screen.screenKey);
                          setErrors((prev) => ({ ...prev, modules: undefined }));
                        }}
                      />

                      <Label className="cursor-pointer text-sm font-normal">
                        {screen.screenName}
                      </Label>
                    </div>
                  ))}
                </div>
                {formData.modules.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 p-3 border rounded-lg bg-gradient-to-br from-blue-50 to-sky-50">
                    {formData.modules.map((module) => (
                      <Badge
                        key={module}
                        className={`${getModuleColor(module)} text-xs`}
                      >
                        {getModuleLabel(module)}
                        <button
                          onClick={() => toggleModuleSelection(module)}
                          className="ml-1.5 hover:bg-black/10 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </CheckboxFieldset>
                {errors.modules && (
                  <p className="text-xs text-red-600">{errors.modules}</p>
                )}
              </div>

              <div className="space-y-3">
                <CheckboxFieldset
                  label="Accepted File Formats"
                  required
                  error={errors.acceptedFormats}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 border rounded-lg bg-gray-50">
                    {["PDF", "JPG", "PNG", "JPEG", "DOC", "DOCX"].map((format) => (
                      <div key={format} className="flex items-center gap-2">
                        <Checkbox
                          id={`format-${format}`}
                          checked={formData.acceptedFormats.includes(format)}
                          onCheckedChange={() => {
                            toggleFormatSelection(format);
                            setErrors((prev) => ({
                              ...prev,
                              acceptedFormats: undefined,
                            }));
                          }}
                          className="h-4 w-4"
                        />

                        <Label
                          htmlFor={`format-${format}`}
                          className="cursor-pointer text-sm font-normal"
                        >
                          {format}
                        </Label>
                      </div>
                    ))}
                  </div>
                </CheckboxFieldset>
                {errors.acceptedFormats && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.acceptedFormats}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Maximum File Size (MB)"
                  required
                  id="maxSize"
                  type="number"
                  min="1"
                  max="50"
                  value={formData.maxSize}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      maxSize: parseInt(e.target.value) || 0,
                    });
                    setErrors((prev) => ({ ...prev, maxSize: undefined }));
                  }}
                />
                {errors.maxSize && (
                  <p className="text-xs text-red-600">{errors.maxSize}</p>
                )}

                <p className="text-xs text-gray-500">
                  Recommended: 5MB for images, 10MB for documents
                </p>
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Expiry Max Limit (Months)"
                  id="expiryMonths"
                  type="number"
                  min="1"
                  max="120"
                  placeholder="e.g., 6"
                  value={formData.expiryMonths || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      expiryMonths: e.target.value
                        ? parseInt(e.target.value)
                        : undefined,
                    })
                  }
                />
                <p className="text-xs text-gray-500">
                  <strong>{formData.name || "Document name"}</strong> must be
                  valid for at least{" "}
                  <strong>{formData.expiryMonths || "[months]"}</strong> months
                  from travel date
                </p>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-gradient-to-br from-gray-50 to-slate-50">
                <div>
                  <Label className="text-sm">Mandatory Document</Label>
                  <p className="text-xs text-gray-600">
                    Make this document required for upload
                  </p>
                </div>
                <Checkbox
                  id="mandatory"
                  checked={formData.mandatory}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, mandatory: checked as boolean })
                  }
                  className="h-4 w-4"
                />
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <Select
                    value={formData.status}
                    onValueChange={(value: "active" | "inactive") =>
                      setFormData({ ...formData, status: value })
                    }
                  >
                    <SelectTriggerUI id="status">
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>
            <DialogFooter className="sticky bottom-0 bg-white border-t sm:border-0 px-6 py-4 sm:p-0 sm:static flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddDialogOpen(false);
                  resetForm();
                }}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddDocument}
                style={{ background: "#5B9BD5" }}
                className="text-white w-full sm:w-auto"
              >
                Add Document Type
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Document Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="w-[60vw] !max-w-[60vw] max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Edit Document Type
              </DialogTitle>
              <DialogDescription>
                Update document type settings and module assignments
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 px-1">
              <div className="space-y-2">
                <InputFieldset
                  label="Document Name"
                  required
                  id="edit-name"
                  placeholder="e.g., Passport Copy"
                  value={editFormData.name}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/[^A-Za-z ]/g, "")
                      .slice(0, 20);

                    setEditFormData({
                      ...editFormData,
                      name: value,
                    });
                  }}
                />
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Document Code"
                  required
                  id="edit-code"
                  placeholder="e.g., DOC_PASSPORT"
                  value={editFormData.code}
                  onChange={(e) => {
                    const value = e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z_]/g, "")
                      .slice(0, 20);

                    setEditFormData({
                      ...editFormData,
                      code: value,
                    });
                  }}
                />
                <p className="text-xs text-gray-500">
                  Auto-formatted to uppercase with underscores
                </p>
              </div>

              <div className="space-y-3">
                <CheckboxFieldset
                  label="Screens"
                  required
                  description="Select one or more screens where this document can be uploaded"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border rounded-lg bg-gray-50">
                    {screens.map((screen) => (
                      <div
                        key={screen.screenKey}
                        className="flex items-center gap-2"
                      >
                        <Checkbox
                          checked={editFormData.modules.includes(screen.screenKey)}
                          onCheckedChange={() =>
                            toggleModuleSelection(screen.screenKey, true)
                          }
                        />
                        <Label className="cursor-pointer text-sm font-normal">
                          {screen.screenName}
                        </Label>
                      </div>
                    ))}
                  </div>
                  {editFormData.modules.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 p-3 border rounded-lg bg-gradient-to-br from-blue-50 to-sky-50">
                      {editFormData.modules.map((module) => (
                        <Badge
                          key={module}
                          className={`${getModuleColor(module)} text-xs`}
                        >
                          {getModuleLabel(module)}
                          <button
                            onClick={() => toggleModuleSelection(module, true)}
                            className="ml-1.5 hover:bg-black/10 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </CheckboxFieldset>
              </div>

              <div className="space-y-3">
                <CheckboxFieldset
                  label="Accepted File Formats"
                  required
                >
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 border rounded-lg bg-gray-50">
                    {["PDF", "JPG", "PNG", "JPEG", "DOC", "DOCX"].map((format) => (
                      <div key={format} className="flex items-center gap-2">
                        <Checkbox
                          id={`edit-format-${format}`}
                          checked={editFormData.acceptedFormats.includes(format)}
                          onCheckedChange={() =>
                            toggleFormatSelection(format, true)
                          }
                          className="h-4 w-4"
                        />
                        <Label
                          htmlFor={`edit-format-${format}`}
                          className="cursor-pointer text-sm font-normal"
                        >
                          {format}
                        </Label>
                      </div>
                    ))}
                  </div>
                </CheckboxFieldset>
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Maximum File Size (MB)"
                  required
                  id="edit-maxSize"
                  type="number"
                  min="1"
                  max="50"
                  value={editFormData.maxSize}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      maxSize: parseInt(e.target.value) || 5,
                    })
                  }
                />
                <p className="text-xs text-gray-500">
                  Recommended: 5MB for images, 10MB for documents
                </p>
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Expiry Max Limit (Months)"
                  id="edit-expiryMonths"
                  type="number"
                  min="1"
                  max="120"
                  placeholder="e.g., 6"
                  value={editFormData.expiryMonths || ""}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      expiryMonths: e.target.value
                        ? parseInt(e.target.value)
                        : undefined,
                    })
                  }
                />
                <p className="text-xs text-gray-500">
                  <strong>{editFormData.name || "Document name"}</strong> must be
                  valid for at least{" "}
                  <strong>{editFormData.expiryMonths || "[months]"}</strong> months
                  from travel date
                </p>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-gradient-to-br from-gray-50 to-slate-50">
                <div>
                  <Label className="text-sm">Mandatory Document</Label>
                  <p className="text-xs text-gray-600">
                    Make this document required for upload
                  </p>
                </div>
                <Checkbox
                  id="edit-mandatory"
                  checked={editFormData.mandatory}
                  onCheckedChange={(checked) =>
                    setEditFormData({
                      ...editFormData,
                      mandatory: checked as boolean,
                    })
                  }
                  className="h-4 w-4"
                />
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <Select
                    value={editFormData.status}
                    onValueChange={(value: "active" | "inactive") =>
                      setEditFormData({ ...editFormData, status: value })
                    }
                  >
                    <SelectTriggerUI id="edit-status">
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingDocument(null);
                }}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateDocument}
                disabled={
                  !editFormData.name ||
                  !editFormData.code ||
                  editFormData.modules.length === 0 ||
                  editFormData.acceptedFormats.length === 0
                }
                style={{ background: "#5B9BD5" }}
                className="text-white w-full sm:w-auto"
              >
                Update Document Type
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
